import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const WATERMARKS = {
  draft: { text: "DRAFT — NOT APPROVED", color: [180, 180, 180] },
  submitted: { text: "DRAFT — NOT APPROVED", color: [180, 180, 180] },
  changes_requested: { text: "DRAFT — NOT APPROVED", color: [180, 180, 180] },
  approved: { text: "APPROVED — NOT YET PUBLISHED", color: [212, 175, 55] },
  rejected: { text: "REJECTED — DO NOT USE", color: [230, 57, 70] },
  published: null, // "OFFICIAL PUBLISHED SCHEDULE" shown in header instead
  updated_after_publication: null,
};

async function loadLogo() {
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = "/hos-logo.webp";
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
    return canvas.toDataURL("image/png");
  } catch (_) { return null; }
}

function fmtTime(t) { return t || ""; }

function shiftHours(s) {
  try {
    const [h1, m1] = s.start_time.split(":").map(Number);
    const [h2, m2] = s.end_time.split(":").map(Number);
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1) - (Number(s.break_minutes) || 0);
    if (mins < 0) mins = 0;
    return mins / 60;
  } catch { return 0; }
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function humanDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return iso; }
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export async function generateWeeklyPDF({
  schedule, weekStart, storeName = "House of Spells – Times Square",
  employees, filters = {}, generatedBy = "", compact = false, includeNotes = true, includeBreaks = true,
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 30;
  const status = schedule?.status || "draft";
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const logo = await loadLogo();

  // Header
  if (logo) {
    try { doc.addImage(logo, "PNG", marginX, 24, 46, 46); } catch (_) {}
  }
  doc.setFont("times", "bold");
  doc.setFontSize(15);
  doc.setTextColor(20, 20, 20);
  doc.text("HOUSE OF SPELLS — TIMES SQUARE", marginX + (logo ? 56 : 0), 42);
  doc.setFontSize(11);
  doc.setTextColor(90, 70, 20);
  doc.text(`Weekly Staff Schedule · ${humanDate(weekStart)} – ${humanDate(addDays(weekStart, 6))}`, marginX + (logo ? 56 : 0), 60);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const rightX = pageW - marginX;
  const metaLines = [
    `Store: ${storeName}`,
    `Status: ${status.replace(/_/g, " ").toUpperCase()}${schedule?.version ? ` · v${schedule.version}` : ""}`,
    `Generated: ${new Date().toLocaleString()}${generatedBy ? ` by ${generatedBy}` : ""}`,
    schedule?.schedule_number ? `Schedule #: ${schedule.schedule_number}` : "",
  ].filter(Boolean);
  metaLines.forEach((line, i) => doc.text(line, rightX, 30 + i * 12, { align: "right" }));

  // Official banner for published
  if (status === "published" || status === "updated_after_publication") {
    doc.setFillColor(15, 90, 40);
    doc.rect(marginX, 76, pageW - marginX * 2, 18, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text("OFFICIAL PUBLISHED SCHEDULE", pageW / 2, 89, { align: "center" });
  }

  // Filter summary line
  const filterParts = [];
  if (filters.department && filters.department !== "All") filterParts.push(`Dept: ${filters.department}`);
  if (filters.role) filterParts.push(`Role: ${filters.role}`);
  if (filters.employees?.length) filterParts.push(`${filters.employees.length} employees selected`);
  if (filterParts.length) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Filters — ${filterParts.join(" · ")}`, marginX, 108);
  }

  // Build rows
  const empById = {};
  for (const e of employees || []) empById[e.id] = e;
  const shifts = (schedule?.shifts || []);
  const empShifts = {};
  shifts.forEach((s) => {
    (empShifts[s.employee_id] = empShifts[s.employee_id] || {});
    (empShifts[s.employee_id][s.date] = empShifts[s.employee_id][s.date] || []).push(s);
  });

  // apply filters
  const empIds = Object.keys(empShifts).filter((eid) => {
    if (filters.employees?.length && !filters.employees.includes(eid)) return false;
    if (filters.department && filters.department !== "All") {
      const hasDept = (empShifts[eid][Object.keys(empShifts[eid])[0]] || []).some((s) => s.department === filters.department);
      if (!hasDept) return false;
    }
    if (filters.role) {
      const hasRole = Object.values(empShifts[eid]).flat().some((s) => (s.role || "").toLowerCase() === filters.role.toLowerCase());
      if (!hasRole) return false;
    }
    return true;
  });

  const body = empIds.map((eid) => {
    const emp = empById[eid];
    const label = emp ? `${emp.name}\n${emp.role || ""}${emp.department ? " · " + emp.department : ""}` : eid;
    let totalHrs = 0;
    const dayCells = days.map((d) => {
      const list = (empShifts[eid][d] || []);
      if (list.length === 0) return "OFF";
      return list.map((s) => {
        totalHrs += shiftHours(s);
        const parts = [`${fmtTime(s.start_time)}–${fmtTime(s.end_time)}`];
        if (!compact) {
          const detail = [s.role, s.shift_type].filter(Boolean).join(" · ");
          if (detail) parts.push(detail);
          if (includeBreaks && s.break_minutes) parts.push(`Break ${s.break_minutes}m`);
          if (includeNotes && s.notes) parts.push(`"${s.notes}"`);
        }
        return parts.join("\n");
      }).join("\n\n");
    });
    return [label, ...dayCells, `${totalHrs.toFixed(1)}h`];
  });

  autoTable(doc, {
    startY: 120,
    head: [["Employee", ...DAY_LABELS.map((d, i) => `${d}\n${humanDate(days[i])}`), "Hours"]],
    body: body.length > 0 ? body : [["(No matching shifts)", ...DAY_LABELS.map(() => ""), ""]],
    styles: { fontSize: compact ? 7 : 8, cellPadding: compact ? 3 : 5, valign: "top", overflow: "linebreak" },
    headStyles: { fillColor: [16, 23, 38], textColor: [212, 175, 55], halign: "center" },
    columnStyles: {
      0: { cellWidth: 110, fontStyle: "bold" },
      8: { cellWidth: 50, halign: "center", fontStyle: "bold", textColor: [212, 175, 55] },
    },
    margin: { left: marginX, right: marginX },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index >= 1 && data.column.index <= 7) {
        if (String(data.cell.raw).trim() === "OFF") {
          data.cell.styles.textColor = [160, 160, 160];
          data.cell.styles.fontStyle = "italic";
          data.cell.styles.halign = "center";
        }
      }
    },
    didDrawPage: () => {
      const wm = WATERMARKS[status];
      if (wm) {
        doc.saveGraphicsState?.();
        doc.setTextColor(...wm.color);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(80);
        doc.setGState && doc.setGState(new doc.GState({ opacity: 0.15 }));
        doc.text(wm.text, pageW / 2, doc.internal.pageSize.getHeight() / 2, { align: "center", angle: 30 });
        doc.setGState && doc.setGState(new doc.GState({ opacity: 1 }));
        doc.restoreGraphicsState?.();
      }
    },
  });

  // Footer summary
  const finalY = doc.lastAutoTable.finalY + 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  const totalEmployees = empIds.length;
  const totalShifts = shifts.filter((s) => (!filters.employees?.length || filters.employees.includes(s.employee_id))).length;
  const totalHours = shifts.reduce((sum, s) => sum + shiftHours(s), 0);
  const footer = [
    `Total scheduled employees: ${totalEmployees}`,
    `Total scheduled shifts: ${totalShifts}`,
    `Total scheduled labor hours: ${totalHours.toFixed(1)}h`,
    schedule?.submitted_by_name ? `Prepared by: ${schedule.submitted_by_name} · Submitted ${schedule.submitted_at ? new Date(schedule.submitted_at).toLocaleString() : ""}` : "",
    schedule?.approved_by_name ? `GM Approval: ${schedule.approved_by_name} · ${schedule.approved_at ? new Date(schedule.approved_at).toLocaleString() : ""}` : "",
    schedule?.published_by_name ? `Published by: ${schedule.published_by_name} · ${schedule.published_at ? new Date(schedule.published_at).toLocaleString() : ""}` : "",
    schedule?.version ? `Version: ${schedule.version}` : "",
  ].filter(Boolean);
  footer.forEach((line, i) => doc.text(line, marginX, finalY + i * 12));

  // Filename
  const deptPart = filters.department && filters.department !== "All" ? `-${filters.department.replace(/\s+/g, "-")}` : "";
  const filename = `House-of-Spells-Times-Square${deptPart}-Weekly-Schedule-${weekStart}.pdf`;

  doc.save(filename);
  return filename;
}
